import React, { ComponentProps } from 'react';

interface PolybankLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'white' | 'dark';
}

export function PolybankLogo({
  className = '',
  size = 'md',
  variant = 'default',
}: PolybankLogoProps) {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24',
  };

  const colors = {
    default: {
      primary: '#3B82F6',
      secondary: '#10B981',
      accent: '#8B5CF6',
      text: '#1F2937',
    },
    white: {
      primary: '#ffffff',
      secondary: '#f3f4f6',
      accent: '#e5e7eb',
      text: '#ffffff',
    },
    dark: {
      primary: '#1F2937',
      secondary: '#374151',
      accent: '#4B5563',
      text: '#1F2937',
    },
  };

  const colorScheme = colors[variant];

  return (
    <svg
      className={`${sizes[size]} ${className}`}
      viewBox='0 0 120 120'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      {/* Background Circle */}
      <circle
        cx='60'
        cy='60'
        r='58'
        fill='url(#gradient1)'
        stroke={colorScheme.primary}
        strokeWidth='2'
      />

      {/* Interconnected Nodes representing multiple banks */}
      <g opacity='0.9'>
        {/* Central Node */}
        <circle cx='60' cy='60' r='12' fill={colorScheme.primary} />
        <circle cx='60' cy='60' r='8' fill='white' />
        <circle cx='60' cy='60' r='4' fill={colorScheme.primary} />

        {/* Surrounding Nodes */}
        <circle cx='35' cy='35' r='8' fill={colorScheme.secondary} />
        <circle cx='85' cy='35' r='8' fill={colorScheme.accent} />
        <circle cx='35' cy='85' r='8' fill={colorScheme.accent} />
        <circle cx='85' cy='85' r='8' fill={colorScheme.secondary} />

        {/* Connection Lines */}
        <line
          x1='35'
          y1='35'
          x2='60'
          y2='60'
          stroke={colorScheme.primary}
          strokeWidth='2'
          opacity='0.6'
        />
        <line
          x1='85'
          y1='35'
          x2='60'
          y2='60'
          stroke={colorScheme.primary}
          strokeWidth='2'
          opacity='0.6'
        />
        <line
          x1='35'
          y1='85'
          x2='60'
          y2='60'
          stroke={colorScheme.primary}
          strokeWidth='2'
          opacity='0.6'
        />
        <line
          x1='85'
          y1='85'
          x2='60'
          y2='60'
          stroke={colorScheme.primary}
          strokeWidth='2'
          opacity='0.6'
        />

        {/* Curved connection between outer nodes */}
        <path
          d='M 35 35 Q 60 20 85 35'
          stroke={colorScheme.secondary}
          strokeWidth='1.5'
          fill='none'
          opacity='0.4'
        />
        <path
          d='M 85 35 Q 100 60 85 85'
          stroke={colorScheme.accent}
          strokeWidth='1.5'
          fill='none'
          opacity='0.4'
        />
        <path
          d='M 85 85 Q 60 100 35 85'
          stroke={colorScheme.secondary}
          strokeWidth='1.5'
          fill='none'
          opacity='0.4'
        />
        <path
          d='M 35 85 Q 20 60 35 35'
          stroke={colorScheme.accent}
          strokeWidth='1.5'
          fill='none'
          opacity='0.4'
        />
      </g>

      {/* Inner rings for depth */}
      <circle
        cx='60'
        cy='60'
        r='28'
        fill='none'
        stroke={colorScheme.primary}
        strokeWidth='1'
        opacity='0.2'
      />
      <circle
        cx='60'
        cy='60'
        r='38'
        fill='none'
        stroke={colorScheme.secondary}
        strokeWidth='1'
        opacity='0.15'
      />

      {/* Gradient Definitions */}
      <defs>
        <linearGradient id='gradient1' x1='0%' y1='0%' x2='100%' y2='100%'>
          <stop offset='0%' stopColor={colorScheme.primary} stopOpacity='0.1' />
          <stop
            offset='50%'
            stopColor={colorScheme.secondary}
            stopOpacity='0.05'
          />
          <stop
            offset='100%'
            stopColor={colorScheme.accent}
            stopOpacity='0.1'
          />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function PolybankWordmark({
  className = '',
  variant = 'default',
  logoSize = 'md',
}: {
  className?: string;
  variant?: 'default' | 'white' | 'dark';
  logoSize?: ComponentProps<typeof PolybankLogo>['size'];
}) {
  const textColors = {
    default: 'text-gray-900',
    white: 'text-white',
    dark: 'text-gray-900',
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <PolybankLogo size={logoSize} variant={variant} />
      <span className={`text-2xl font-bold ${textColors[variant]}`}>
        Poly<span className='text-blue-600'>bank</span>
      </span>
    </div>
  );
}
